import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, Receipt, TrendingUp, Wallet, Building2, ChevronDown, ChevronRight } from 'lucide-react';
import { CLAIM_STATUS_META, money } from '../../lib/rcm';
import { ClaimScrubPanel } from './ClaimScrubPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const m = CLAIM_STATUS_META[status] || CLAIM_STATUS_META.draft;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border ${m.cls}`}>{m.label}</span>;
};

export const AdminClaimsPanel = () => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/rcm/overview`, getAuthHeader());
      setData(res.data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { (async () => { await load(); })(); }, [load]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-cn-primary" /></div>;
  if (!data) return <p className="text-sm text-slate-500">No claims data available.</p>;

  const s = data.summary || {};
  return (
    <div data-testid="admin-claims-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Receipt, label: 'Total billed', value: money(s.total_billed), sub: `${s.claim_count || 0} claims` },
          { icon: TrendingUp, label: 'Collected', value: money(s.total_collected), sub: 'across all clinicians' },
          { icon: Wallet, label: 'Outstanding A/R', value: money(s.total_outstanding), sub: 'awaiting payment' },
          { icon: Building2, label: 'Platform fees', value: money(s.platform_fees), sub: `${Math.round((s.platform_fee_pct || 0.12) * 100)}% of collected` },
        ].map((k, i) => (
          <div key={i} className="rounded-2xl border border-cn-border p-4 bg-cn-bg/40" data-testid={`admin-rcm-kpi-${i}`}>
            <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{k.label}</span><k.icon className="w-4 h-4 text-cn-primary" /></div>
            <div className="mt-1.5 font-display font-bold text-xl text-cn-text dark:text-white">{k.value}</div>
            <div className="text-xs text-cn-text-muted">{k.sub}</div>
          </div>
        ))}
      </div>

      {s.by_status && (
        <div className="flex flex-wrap gap-2 mt-5">
          {Object.entries(s.by_status).filter(([, n]) => n > 0).map(([k, n]) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-xs"><StatusBadge status={k} /> <b>{n}</b></span>
          ))}
        </div>
      )}

      <div className="mt-6 overflow-x-auto rounded-2xl border border-cn-border" data-testid="admin-claims-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-cn-text-muted border-b border-cn-border bg-cn-bg/50">
            <th className="p-3 w-8"></th><th className="p-3">Claim #</th><th className="p-3">Clinician</th><th className="p-3">Patient</th><th className="p-3">Payer</th>
            <th className="p-3 text-right">Charges</th><th className="p-3 text-right">Balance</th><th className="p-3">Status</th><th className="p-3">EDI</th>
          </tr></thead>
          <tbody>
            {(data.claims || []).map((c) => {
              const ti = c.transmission_info;
              const transmitted = ti?.status === 'transmitted';
              const isOpen = expanded === c.id;
              return (
                <React.Fragment key={c.id}>
                  <tr
                    className="border-b border-cn-border/40 last:border-0 hover:bg-cn-bg/60 cursor-pointer"
                    onClick={() => setExpanded(isOpen ? null : c.id)}
                    data-testid={`admin-claim-row-${c.id}`}
                  >
                    <td className="p-3 text-cn-text-muted">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                    <td className="p-3 font-mono text-xs text-cn-primary">{c.claim_number}</td>
                    <td className="p-3">{c.clinician_name}</td>
                    <td className="p-3 text-cn-text-muted">{c.patient_name}</td>
                    <td className="p-3 text-cn-text-muted">{c.billing_type === 'self_pay' ? 'Self-pay' : (c.payer_name || '—')}</td>
                    <td className="p-3 text-right font-semibold">{money(c.total_charges)}</td>
                    <td className="p-3 text-right">{money(c.balance)}</td>
                    <td className="p-3"><StatusBadge status={c.status} /></td>
                    <td className="p-3">
                      {transmitted
                        ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-cn-primary"><Building2 className="w-3 h-3" /> Transmitted</span>
                        : <span className="text-[10px] text-cn-text-muted">{c.billing_type === 'self_pay' ? 'Self-pay' : '—'}</span>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-cn-bg/40 border-b border-cn-border/40" data-testid={`admin-claim-expanded-${c.id}`}>
                      <td></td>
                      <td colSpan={8} className="p-4 space-y-4">
                        {/* AI denial-prevention scrub — runnable on every claim, regardless of status */}
                        <ClaimScrubPanel
                          claimId={c.id}
                          initialResult={c.last_scrub || null}
                          getAuthHeader={getAuthHeader}
                        />

                        {transmitted && (
                          <div data-testid={`admin-claim-transmission-${c.id}`}>
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-cn-text-muted"><Building2 className="w-3.5 h-3.5 text-cn-primary" /> EDI 837P transmission</span>
                              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300">Simulated</span>
                            </div>
                            <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-cn-text-muted max-w-lg">
                              <div className="flex justify-between"><span>Clearinghouse</span><span className="font-semibold text-cn-text dark:text-white">{ti.clearinghouse}</span></div>
                              <div className="flex justify-between"><span>Transmission ID</span><span className="font-mono">{(ti.transmission_id || '').slice(0, 8)}</span></div>
                              {ti.transmitted_at && <div className="flex justify-between sm:col-span-2"><span>Transmitted at</span><span className="font-mono">{new Date(ti.transmitted_at).toLocaleString()}</span></div>}
                            </div>
                            {ti.edi_preview && (
                              <pre className="mt-2 text-[10px] leading-relaxed font-mono bg-slate-900 text-slate-100 rounded-lg p-2.5 overflow-x-auto max-h-40" data-testid={`admin-claim-edi-${c.id}`}>{ti.edi_preview}</pre>
                            )}
                            <p className="mt-1.5 text-[10px] text-cn-text-muted">Demo only — no real clearinghouse (Change Healthcare / Availity / Stedi) transmission yet.</p>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {(data.claims || []).length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-cn-text-muted text-sm">No claims have been created yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminClaimsPanel;
